import React, { useMemo, useReducer, useCallback, useEffect, useState } from 'react'
import { Map } from 'immutable'
import { Grid, Link } from '@material-ui/core'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { Link as RouterLink, useParams } from '@reach/router'

import Snackbar from '_components/snackbar'
import ConfirmationModal from '_components/confirmation-modal'
import Button from '_components/button'
import AddUserModal from '_/components/accordion/add-users/add-user-modal'
import useModal from '_hooks/use-toggle'
import { useToast, DEFAULT_ERROR_MESSAGE, DEFAULT_SUCCESS_MESSAGE } from '_hooks/use-toast'
import { REFERA_AXIOS_ERROR_META } from '_services/requests'
import {
  addAgencyStaffUser,
  updateAgencyStaffUser,
  deleteAgencyStaffUser,
  inactivateAgencyStaffUser,
  ADD_AGENCY_STAFF_USER,
  UPDATE_AGENCY_STAFF_USER,
} from '_modules/agency/actions'
import {
  addProviderStaffUser,
  updateProviderStaffUser,
  deleteProviderStaffUser,
  ADD_PROVIDER_STAFF_USER,
  UPDATE_PROVIDER_STAFF_USER,
} from '_modules/provider/actions'
import useFetchCall from '_hooks/use-fetch-call'
import StaffUser from '_models/staff-user'

import Accordion from '../index'

import useStyles from './styles'
import UserInfo from './user-info'
import {
  reducer,
  INITIAL_STATE,
  ADD_USER,
  HANDLE_CURRENT_USER,
  HANDLE_CURRENT_USER_BLUR,
  UPDATE_USER,
  DELETE_USER,
} from './reducer'
import { AGENCY_FIELDS } from '_/views/agency/constants'

/** Mesmo `error_code` do back (`agency_staff_cannot_delete_protected`) — abre modal de inativar. */
const ERR_AGENCY_STAFF_CANNOT_DELETE_PROTECTED = 'agency_staff_cannot_delete_protected'

const staffErrorPayload = err => {
  const raw = err?.response?.data != null ? err.response.data : err
  if (raw == null || typeof raw !== 'object') return {}
  const rest = { ...raw }
  delete rest[REFERA_AXIOS_ERROR_META]
  return rest
}

const staffRequestHttpStatus = err =>
  err?.[REFERA_AXIOS_ERROR_META]?.status ?? err?.response?.status

/** Erro: `error_message` do back, ou `DEFAULT_ERROR_MESSAGE`. Status ≠ 400 → sempre genérico. */
const staffCrudToastMessage = err => {
  const status = staffRequestHttpStatus(err)
  if (status != null && status !== 400) {
    return DEFAULT_ERROR_MESSAGE
  }
  const raw = err?.response?.data != null ? err.response.data : err
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (t && t.length < 500) {
      const head = t.slice(0, 100).toLowerCase()
      if (!/<!doctype\s+html|<html[\s>]/.test(head)) return t
    }
    return DEFAULT_ERROR_MESSAGE
  }
  if (Map.isMap(raw)) {
    return raw.get('errorMessage') || raw.get('error_message') || DEFAULT_ERROR_MESSAGE
  }
  const body = staffErrorPayload(err)
  return body?.error_message || body?.errorMessage || DEFAULT_ERROR_MESSAGE
}

const getStaffSortName = user => {
  if (!user) return ''
  const name = user.get ? user.get('name') : user.name
  const pr = user.get ? user.get('personResponsibleName') : user.personResponsibleName
  const prSnake = user.get ? user.get('person_responsible_name') : user.person_responsible_name
  const fullName = user.get ? user.get('fullName') : user.fullName
  return String(name || pr || prSnake || fullName || '')
    .trim()
    .toLocaleLowerCase('pt-BR')
}

/** Ordenação de exibição só no front: ativos primeiro (A–Z), depois inativos (A–Z). */
const isStaffRowInactive = user => {
  if (!user) return false
  const raw = user.toJS ? user.toJS() : user
  const v = raw.isActive ?? raw.is_active
  return v === false || v === 'false'
}

const AddUsers = ({
  isProvider,
  staffUsers,
  isCreating,
  setFieldValue,
  readOnly,
  fallbackUser,
  ...otherProps
}) => {
  const styles = useStyles()
  const { showToast } = useToast()
  const dispatch = useDispatch()
  const agencyId = useSelector(s => s.agencies.currentAgency)
  const [modalOpen, handleModal] = useModal()
  const [values, dispatchLocal] = useReducer(reducer, INITIAL_STATE)
  const [isEditingProvider, handleIsEditing] = useModal()
  const [userPendingDelete, setUserPendingDelete] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false)
  const [deactivateStaffId, setDeactivateStaffId] = useState(null)
  const [isDeactivatingUser, setIsDeactivatingUser] = useState(false)
  const addUser = isProvider ? addProviderStaffUser : addAgencyStaffUser
  const updateUser = isProvider ? updateProviderStaffUser : updateAgencyStaffUser
  const deleteUser = deleteProviderStaffUser
  const addUserAction = isProvider ? ADD_PROVIDER_STAFF_USER.ACTION : ADD_AGENCY_STAFF_USER.ACTION
  const updateUserAction = isProvider
    ? UPDATE_PROVIDER_STAFF_USER.ACTION
    : UPDATE_AGENCY_STAFF_USER.ACTION
  const url = useParams()

  const sortedStaffUsers = useMemo(() => {
    if (!staffUsers?.length) return staffUsers
    return [...staffUsers].sort((a, b) => {
      const ia = isStaffRowInactive(a)
      const ib = isStaffRowInactive(b)
      if (ia !== ib) return ia ? 1 : -1
      return getStaffSortName(a).localeCompare(getStaffSortName(b), 'pt-BR', {
        sensitivity: 'base',
      })
    })
  }, [staffUsers])

  const sortedDraftUsers = useMemo(() => {
    if (!values.users?.length) return values.users
    return [...values.users].sort((a, b) =>
      String(a.fullName || '')
        .trim()
        .localeCompare(String(b.fullName || '').trim(), 'pt-BR', { sensitivity: 'base' })
    )
  }, [values.users])

  const handleInputChange = useCallback(event => {
    const { value, name } = event.target
    dispatchLocal({
      type: HANDLE_CURRENT_USER,
      payload: { values: { value, name } },
    })
  }, [])

  const handleBlur = useCallback(event => {
    const { value, name } = event.target
    dispatchLocal({
      type: HANDLE_CURRENT_USER_BLUR,
      payload: { value, name },
    })
  }, [])

  const formHandleValues = useMemo(
    () => ({
      onChange: handleInputChange,
      onBlur: handleBlur,
      errors: values.error,
      touched: values.touched,
      values: values.currentUser,
    }),
    [handleBlur, handleInputChange, values]
  )

  const handleSuccessModals = useCallback(() => {
    handleIsEditing()
    handleModal()
  }, [handleIsEditing, handleModal])

  const handleSubmit = useCallback(
    value => {
      if (isCreating) {
        dispatchLocal({
          type: !isEditingProvider ? ADD_USER : UPDATE_USER,
          payload: values,
        })
        handleSuccessModals()
        return
      }
      if (!value?.id) {
        dispatch(addUser(value))
        return
      }
      // eslint-disable-next-line no-unused-vars
      const { getServiceProviderId, registrationConfirmed, termsAccepted, ...payload } = value
      dispatch(updateUser(payload))
    },
    [addUser, dispatch, handleSuccessModals, isCreating, isEditingProvider, updateUser, values]
  )

  const [isAddingUser] = useFetchCall(addUserAction, handleSuccessModals)
  const [isUpdatingUser] = useFetchCall(updateUserAction, handleSuccessModals)
  const isLoading = isAddingUser || isUpdatingUser

  const handleCloseDeleteConfirm = useCallback(() => {
    setDeleteConfirmOpen(false)
    setUserPendingDelete(null)
  }, [])

  const handleCloseDeactivateModal = useCallback(() => {
    setDeactivateModalOpen(false)
    setDeactivateStaffId(null)
  }, [])

  const handleDeleteUser = useCallback(user => {
    setUserPendingDelete(user)
    setDeleteConfirmOpen(true)
  }, [])

  const handleConfirmDeactivateUser = useCallback(async () => {
    if (!deactivateStaffId || !agencyId) {
      handleCloseDeactivateModal()
      return
    }
    setIsDeactivatingUser(true)
    try {
      await dispatch(inactivateAgencyStaffUser(deactivateStaffId))
      showToast({
        type: 'success',
        message: DEFAULT_SUCCESS_MESSAGE,
      })
    } catch (err) {
      showToast({
        type: 'error',
        message: staffCrudToastMessage(err),
      })
    } finally {
      setIsDeactivatingUser(false)
      handleCloseDeactivateModal()
    }
  }, [agencyId, deactivateStaffId, dispatch, handleCloseDeactivateModal, showToast])

  const handleConfirmDeleteUser = useCallback(async () => {
    const user = userPendingDelete
    if (!user) {
      handleCloseDeleteConfirm()
      return
    }
    if (isCreating) {
      dispatchLocal({
        type: DELETE_USER,
        payload: { user },
      })
      setFieldValue(AGENCY_FIELDS.STAFF_USERS, values.users)
      handleCloseDeleteConfirm()
      return
    }
    if (isProvider) {
      try {
        await dispatch(deleteUser(user.id))
        showToast({ type: 'success', message: DEFAULT_SUCCESS_MESSAGE })
      } catch (err) {
        showToast({
          type: 'error',
          message: staffCrudToastMessage(err),
        })
      }
      handleCloseDeleteConfirm()
      return
    }
    try {
      await dispatch(deleteAgencyStaffUser(user.id))
      showToast({ type: 'success', message: DEFAULT_SUCCESS_MESSAGE })
    } catch (err) {
      const data = staffErrorPayload(err)
      const code = data?.error_code
      const toastMsg = staffCrudToastMessage(err)
      showToast({ type: 'error', message: toastMsg })
      if (code === ERR_AGENCY_STAFF_CANNOT_DELETE_PROTECTED) {
        setDeactivateStaffId(user.id)
        setDeactivateModalOpen(true)
      }
    }
    handleCloseDeleteConfirm()
  }, [
    userPendingDelete,
    isCreating,
    isProvider,
    dispatchLocal,
    setFieldValue,
    values.users,
    dispatch,
    deleteUser,
    handleCloseDeleteConfirm,
    agencyId,
    showToast,
  ])

  useEffect(() => {
    if (isCreating && values.users) {
      setFieldValue(AGENCY_FIELDS.STAFF_USERS, values.users)
    }
  }, [isCreating, setFieldValue, values.users])

  return (
    <Accordion
      id="usuarios-cadastrados"
      title="Usuários cadastrados"
      help="Dados de login"
      className={styles.container}
      {...otherProps}
    >
      <Grid className={styles.userList}>
        {isCreating &&
          sortedDraftUsers?.map((user, index) => (
            <UserInfo
              key={user.fullName}
              user={user}
              index={index}
              handleDeleteUser={handleDeleteUser}
              isProvider={isProvider}
            />
          ))}
        {sortedStaffUsers?.map(user => (
          <UserInfo
            key={user.id}
            user={user}
            index={user.id}
            handleDeleteUser={handleDeleteUser}
            isProvider={isProvider}
          />
        ))}
        {!isCreating && (!staffUsers || staffUsers.length === 0) && fallbackUser && (
          <UserInfo
            key="fallback-user"
            user={fallbackUser}
            index={0}
            handleDeleteUser={handleDeleteUser}
            isProvider={isProvider}
          />
        )}
      </Grid>
      {!readOnly && (
        <Link
          key="create-user-button"
          to={`/imobiliaria/${url.agencyId}/criar-usuario`}
          component={RouterLink}
        >
          <Button className={styles.button} variant="contained" color="primary">
            Criar usuário
          </Button>
        </Link>
      )}
      {modalOpen && (
        <AddUserModal
          modalOpen={modalOpen}
          handleModal={handleModal}
          handleSubmit={handleSubmit}
          disabledSubmit={Object.values(values.error).length > 0}
          isProvider={isProvider}
          isLoading={isLoading}
          {...formHandleValues}
        />
      )}
      <ConfirmationModal
        isOpen={deleteConfirmOpen}
        handleClose={handleCloseDeleteConfirm}
        description="Você tem certeza que deseja excluir este usuário?"
        handleConfirmClick={handleConfirmDeleteUser}
      />
      <ConfirmationModal
        isOpen={deactivateModalOpen}
        handleClose={handleCloseDeactivateModal}
        description="Não é possível excluir este usuário. Gostaria de torná-lo inativo?"
        handleConfirmClick={handleConfirmDeactivateUser}
        disabled={isDeactivatingUser}
        variant="warning"
      />
      <Snackbar action={[addUserAction, updateUserAction]} />
    </Accordion>
  )
}

AddUsers.propTypes = {
  isProvider: PropTypes.bool,
  isCreating: PropTypes.bool,
  staffUsers: PropTypes.arrayOf(PropTypes.instanceOf(StaffUser)),
  setFieldValue: PropTypes.func,
  readOnly: PropTypes.bool,
  fallbackUser: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }),
}

AddUsers.defaultProps = {
  isProvider: false,
  isCreating: false,
  staffUsers: [],
  setFieldValue: () => {},
  readOnly: false,
  fallbackUser: null,
}

export default React.memo(AddUsers)
